/**
 * Возврат после входа: `?returnUrl=` страницы входа и SAML-колбэка.
 *
 * Front и ControlPanel живут на одном origin: админка обслуживает `/controlpanel/*`, Front — всё остальное. Адрес
 * возврата приходит из строки запроса, поэтому санитайзер защищает от open-redirect (`//evil`, `javascript:`, чужой
 * хост), от ухода в соседнее приложение (`?returnUrl=/controlpanel/...` во Front) и от петли на страницу входа.
 * Правила приложения — параметрами: ядро не знает ни своего basePath, ни соседей.
 */

export interface IReturnUrlOptions {
    /** Базовый путь роутера приложения (`/controlpanel`); корень — `''` или `'/'`. */
    basePath: string;
    /**
     * Префиксы других приложений на том же origin. Во Front — `['/controlpanel']`; в админке список пуст: проверка
     * basePath сама отбрасывает всё, что не в `/controlpanel/*`.
     */
    otherAppPrefixes: readonly string[];
}

/** Путь страницы входа от basePath: возврат на неё — петля. */
const LOGIN_PATH = '/login';

/** Базовый путь без завершающего `/`; корень (`'/'`, пусто, незаданная переменная окружения) — `''`. */
export const normalizeBasePath = (raw: unknown): string => {
    if (typeof raw !== 'string') return '';
    if (raw === '' || raw === 'undefined' || raw === '/') return '';
    return raw.endsWith('/') ? raw.slice(0, -1) : raw;
};

const isPrefixMatch = (lower: string, prefix: string): boolean =>
    lower === prefix ||
    lower.startsWith(prefix + '/') ||
    lower.startsWith(prefix + '?') ||
    lower.startsWith(prefix + '#');

/** База разбора: адрес строится только для сверки origin и пути и наружу не уходит. */
const PROBE_ORIGIN = 'https://return-url.invalid';
/** `\` браузер читает как `/` (`/\evil` — чужой хост), табуляцию и переводы строки выбрасывает (`/\t/evil` — `//evil`). */
// eslint-disable-next-line no-control-regex -- управляющие символы и есть то, что отбрасывается
const UNSAFE_CHARS = /[\\\u0000-\u001f\u007f]/;

const decodeSegment = (segment: string): string | null => {
    try {
        return decodeURIComponent(segment);
    } catch {
        return null;
    }
};

/**
 * Путь того же origin так, как его поймёт браузер: раскодированный pathname. null — не путь от корня, `\` и
 * управляющие символы, чужой origin после разбора, `.`/`..` (парсер схлопнул бы их в другой путь —
 * `/../controlpanel/x`), битое кодирование и раскодированное `//` в начале.
 */
export const sameOriginPathname = (raw: string): string | null => {
    if (!raw.startsWith('/') || raw.startsWith('//') || UNSAFE_CHARS.test(raw)) return null;
    let url: URL;
    try {
        url = new URL(raw, PROBE_ORIGIN);
    } catch {
        return null;
    }
    if (url.origin !== PROBE_ORIGIN) return null;
    const segments = raw.split(/[?#]/, 1)[0].split('/').map(decodeSegment);
    if (segments.some((segment) => segment === null || segment === '.' || segment === '..')) return null;
    const pathname = decodeSegment(url.pathname);
    return pathname === null || pathname.startsWith('//') ? null : pathname;
};

/**
 * Санитайзер returnUrl.
 *
 * Принимает:
 * - basename-relative путь (`/persons`) — как есть (форма хранения: basename добавит роутер);
 * - absolute путь с собственным basePath (`/controlpanel/persons`) — нормализуется к basename-relative (`/persons`).
 *
 * Отбрасывает:
 * - не-строку, пустое, не-same-origin;
 * - protocol-relative (`//`), `javascript:`, любые URL не с `/`; `\`, управляющие символы и `.`/`..` в пути;
 * - URL с префиксом другого приложения (`otherAppPrefixes`), в том числе закодированным;
 * - петлю на страницу входа (`/login*` от basePath).
 *
 * Возвращает `null`, если URL невалиден, иначе — basename-relative путь.
 */
export const sanitizeReturnUrl = (raw: string | null | undefined, options: IReturnUrlOptions): string | null => {
    if (typeof raw !== 'string' || raw === '') return null;
    // Префиксы сверяются с путём, который увидит браузер: `%63ontrolpanel` — тоже `/controlpanel`.
    const pathname = sameOriginPathname(raw);
    if (pathname === null) return null;
    const lowerPath = pathname.toLowerCase();

    for (const prefix of options.otherAppPrefixes) {
        if (isPrefixMatch(lowerPath, prefix.toLowerCase())) {
            return null;
        }
    }

    const lower = raw.toLowerCase();
    const basePath = normalizeBasePath(options.basePath);
    let basenameRelative = raw;
    let relativePath = lowerPath;
    if (basePath) {
        const bp = basePath.toLowerCase();
        if (isPrefixMatch(lowerPath, bp)) {
            // basePath закодирован — срезать его со строки нечем.
            if (!isPrefixMatch(lower, bp)) return null;
            basenameRelative = lower === bp ? '/' : raw.slice(basePath.length);
            relativePath = lowerPath.slice(bp.length) || '/';
        }
        // Иначе raw не в собственном basePath — это уже basename-relative путь (хранимая форма returnUrl).
    }

    if (isPrefixMatch(relativePath, LOGIN_PATH)) {
        return null;
    }

    return basenameRelative;
};

/**
 * URL входа с `?returnUrl=` для редиректа ProtectedRoute. Если текущий location — корень, returnUrl не добавляется.
 */
export const buildLoginUrlWithReturnTo = (
    loginPath: string,
    currentPathname: string,
    currentSearch: string,
    currentHash: string,
): string => {
    const from = `${currentPathname}${currentSearch}${currentHash}`;
    if (from === '/' || from === '') return loginPath;
    return `${loginPath}?returnUrl=${encodeURIComponent(from)}`;
};
